import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, AlertCircle, Info } from "lucide-react";
import type { VerifiedReference } from "@/lib/api/verify-references";
import { sanitizeCaselawTitle, humanizeOfficialTitle } from "@/lib/reference-labels";
import { codeForArticle, correctCodeLabel } from "@/lib/article-code-registry";
import { vagueAttributionLabel, type VagueAttribution } from "@/lib/vague-attribution";
import { inForceLabel } from "@/lib/version-in-force";
import { isCitedAsFormerArticle, FORMER_ARTICLE_NOTE } from "@/lib/former-article";
import type { UnnumberedDecision } from "@/lib/unnumbered-caselaw";
import { recentModificationLabel, isPendingRecentModification } from "@/lib/recent-modification";
import {
  CASELAW_SCOPE_NOTE,
  hasCaselawScopeDivergence,
} from "@/lib/caselaw-scope";
import { dateEnClair } from "@/lib/derniere-confirmation";


export { sanitizeCaselawTitle };

/**
 * Le code associé à une référence provient de l'extraction serveur, qui
 * propage le dernier code cité dans le texte (`lastKnownCode`). Une réponse
 * mentionnant d'abord le Code de la construction puis un article de la
 * consommation hérite donc d'un code erroné. Le registre d'appartenance
 * ARTICLE → CODE prime sur cette heuristique lorsqu'il est certain.
 */
export function resolveCodeLabel(ref: Pick<VerifiedReference, "kind" | "code" | "number">): string | undefined {
  if (ref.kind !== "article") return ref.code;
  return codeForArticle(ref.number) ?? ref.code;
}



/**
 * Libellé affiché : les métadonnées officielles retrouvées priment toujours
 * sur la référence telle qu'elle a été citée. Une divergence (date, chambre,
 * numéro) ne doit jamais afficher le libellé demandé comme s'il était vérifié.
 */
export function displayLabel(ref: VerifiedReference): string {
  const raw = (ref.officialTitle ?? "").replace(/\s+/g, " ").trim();
  const official = ref.kind === "caselaw" ? sanitizeCaselawTitle(raw, ref.number) : raw;
  if (official && ref.kind === "caselaw") return humanizeOfficialTitle(official);
  if (ref.status === "mismatch") return humanizeOfficialTitle(official) || `n° ${ref.number}`;
  if (official && official.includes(ref.number)) return humanizeOfficialTitle(correctCodeLabel(official));
  return referenceLabel(ref);
}


/** Explique la divergence en langage clair, sans jargon technique. */
export function mismatchNote(ref: VerifiedReference): string {
  const fields = ref.mismatchFields ?? [];
  const parts: string[] = [];
  if (fields.includes("date")) parts.push("date");
  if (fields.includes("court")) parts.push("formation");
  if (fields.includes("number")) parts.push("numéro");
  const origin = ref.source === "question" ? "référence de la question" : "référence citée";
  return parts.length > 0
    ? `${origin} inexacte : ${parts.join(", ")}`
    : "métadonnées officielles divergentes";
}

/**
 * Dernier rempart avant affichage : le libellé d'une référence ne doit jamais
 * être un fragment de phrase issu de la réponse. Si le champ `raw` reçu du
 * backend ressemble à du texte libre, on reconstruit un identifiant propre à
 * partir des champs structurés.
 */
export function referenceLabel(
  ref: Pick<VerifiedReference, "raw" | "kind" | "code" | "number"> & { sourceLabel?: string },
): string {
  // Source non codifiée (convention, traité) : le registre ARTICLE → CODE
  // français n'a pas à s'y appliquer, sous peine de rattacher « article 24 de
  // la Charte sociale européenne » à un code français.
  if (ref.kind === "article" && ref.sourceLabel) {
    return `article ${ref.number} — ${ref.sourceLabel}`;
  }
  const code = resolveCodeLabel(ref);
  const clean =
    ref.kind === "article"
      ? `article ${ref.number}${code ? ` du ${code}` : ""}`
      : `n° ${ref.number}`;
  const raw = (ref.raw ?? "").replace(/\s+/g, " ").trim();
  if (!raw) return clean;
  const looksLikeSentence = raw.length > 80 || /\s(a|ont|est|sont|que|dans|pour|par)\s/i.test(raw);
  if (looksLikeSentence || !raw.includes(ref.number)) return clean;
  // Le `raw` peut nommer un code contredisant le registre : on préfère alors
  // le libellé reconstruit à partir du numéro d'article.
  if (ref.kind === "article" && code && code !== ref.code) return clean;
  return ref.kind === "caselaw" ? sanitizeCaselawTitle(raw, ref.number) : raw;

}


/**
 * Indicateur sobre de vérification des références juridiques contre la
 * source officielle (Légifrance). Volontairement non intrusif : on n'affiche
 * rien si aucune référence exploitable n'a pu être vérifiée.
 */
const STYLES: Record<
  VerifiedReference["status"],
  {
    icon: typeof CheckCircle2;
    cls: string;
    note?: string | ((r: VerifiedReference) => string);
    label: (r: VerifiedReference) => string;
  }
> = {
  confirmed: {
    icon: CheckCircle2,
    cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
    // Légifrance injoignable : confirmée par sa dernière réponse, datée.
    note: (r) => (r.confirmedOn ? `confirmée sur Légifrance le ${dateEnClair(r.confirmedOn) ?? r.confirmedOn.slice(0, 10)}` : ""),
    label: (r) => displayLabel(r),
  },
  mismatch: {
    icon: AlertCircle,
    cls: "bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30",
    note: (r) => mismatchNote(r),
    label: (r) => displayLabel(r),
  },
  not_found: {
    icon: AlertTriangle,
    cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
    note: (r) =>
      r.kind === "caselaw"
        ? "numéro introuvable dans la base officielle — à vérifier"
        : "non retrouvée dans la base officielle",
    label: (r) => referenceLabel(r),
  },
  abrogated: {
    icon: XCircle,
    cls: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
    note: (r) => (r.officialEndDate ? `abrogé le ${dateEnClair(r.officialEndDate) ?? r.officialEndDate}` : "abrogé ou modifié"),
    label: (r) => displayLabel(r),
  },
  /**
   * QA 09/09/2026, point (c) — deux causes distinctes derrière `unverified` :
   * la base officielle n'a pas été interrogée (référence hors portée locale),
   * ou elle était INJOIGNABLE. Le second cas ne dit RIEN de la référence : il
   * est nommé « vérification indisponible » et n'entre pas dans le score.
   */
  unverified: {
    icon: HelpCircle,
    cls: "bg-muted text-muted-foreground border-border",
    note: (r) =>
      r.unavailable
        ? "vérification indisponible — base officielle injoignable"
        : "non vérifiée automatiquement",
    label: (r) => referenceLabel(r),
  },


  /**
   * Source hors base officielle française (convention internationale, droit de
   * l'Union, décision QPC) : ni confirmée, ni suspecte. Signalée sobrement,
   * jamais comptée comme référence introuvable.
   */
  not_applicable: {
    icon: Info,
    cls: "bg-muted text-muted-foreground border-border",
    note: "hors base officielle française — à recouper",
    label: (r) => referenceLabel(r),
  },
  /**
   * LOT F9b — loi / ordonnance / décret NON CODIFIÉ que la base consolidée ne
   * permet pas de recouper. Aucune incohérence relevée : mention NEUTRE,
   * distincte du « non retrouvée dans la base officielle » réservé aux
   * véritables divergences.
   */
  not_consolidated: {
    icon: Info,
    cls: "bg-muted text-muted-foreground border-border",
    note: "texte non codifié — non recoupé en base consolidée",
    label: (r) => referenceLabel(r),
  },
};

/** Normalise un identifiant pour comparaison souple (chiffres et lettres). */
function normalizeRef(value: string): string {
  return (value || "").toLowerCase().replace(/[^0-9a-z]/g, "");
}

/**
 * AFFICHABILITÉ D'UNE RÉFÉRENCE (QA 29/08/2026).
 *
 * Deux sources de placeholders cassés dans l'encart « Sources » :
 *  - des artefacts d'extraction (« article 1 », « article 2 ») captés sur des
 *    puces ou des énumérations, sans numéro réel ni code ;
 *  - des vérifications techniquement échouées (`unverified`) affichées comme
 *    « article 1648 (vérification indisponible) », c'est-à-dire un signal
 *    négatif sur une citation pourtant exacte.
 *
 * Règle : un identifiant d'article doit compter au moins deux caractères
 * significatifs, et une vérification indisponible n'est montrée que si le
 * libellé reste explicite (code identifié, source nommée, ou jurisprudence).
 */
export function isDisplayableReference(
  ref: Pick<VerifiedReference, "kind" | "code" | "number" | "status"> & { sourceLabel?: string },
): boolean {
  // Seuls les articles non vérifiés et à un seul caractère sont traités
  // comme des artefacts d'extraction (puces, énumérations). Une référence
  // recoupée officiellement, même à un chiffre (article 9, article 4…),
  // doit rester visible.
  if (ref.status === "unverified" && ref.kind === "article" && normalizeRef(ref.number).length < 2) return false;
  if (ref.status !== "unverified") return true;
  return ref.kind === "caselaw" || !!ref.sourceLabel || !!resolveCodeLabel(ref);
}



/**
 * Une référence n'est « réellement citée » que si le corps de la réponse la
 * mobilise : numéro d'article, numéro de pourvoi, ou appel de note (exposant
 * ou balise [S#]). Sans quoi l'afficher laisse croire qu'elle a soutenu la
 * réponse alors qu'elle n'a jamais été utilisée.
 */
export function isReallyCited(
  ref: Pick<VerifiedReference, "number" | "raw" | "kind">,
  body: string,
): boolean {
  const content = body || "";
  if (!content) return true;
  if (/\[S\d{1,2}\]/.test(content) || /[¹²³⁴⁵⁶⁷⁸⁹⁰]/.test(content)) return true;
  const number = (ref.number || "").trim();
  if (!number) return false;
  const haystack = normalizeRef(content);
  return haystack.includes(normalizeRef(number));
}

/**
 * SÉLECTION DES RÉFÉRENCES AFFICHÉES — extraite du rendu pour que le compteur
 * du bloc « Sources » (QA 30/08/2026, BUG 5) porte exactement sur la liste
 * réellement montrée, et non sur le nombre de sources injectées en amont.
 */
/** Clé de rapprochement d'un libellé libre (« article 65 ») sur un numéro. */
export function numberKeyOf(raw: string): string {
  const m = (raw || "").match(/(?:[LRDA]\.?\s?)?\d[\d\-.]*/);
  return m ? normalizeRef(m[0]) : "";
}

export function selectDisplayedReferences(
  references: readonly VerifiedReference[] | undefined,
  body?: string,
): VerifiedReference[] {
  const all = references ?? [];
  // Les divergences restent toujours visibles : elles alertent l'utilisateur.
  // QA 25/09/2026 — un verdict défavorable (introuvable, abrogé) porté par une
  // référence extraite de la réponse reste visible même si une réécriture
  // différée du texte l'a retirée : jamais d'effacement silencieux.
  const scoped =
    body === undefined
      ? all
      : all.filter(
          (r) =>
            r.status === "mismatch" ||
            ((r.status === "not_found" || r.status === "abrogated") && r.source === "answer") ||
            isReallyCited(r, body),
        );

  // Dédoublonnage : un même article/arrêt peut être extrait deux fois avec des
  // statuts différents (par ex. « unverified » et « confirmed »). On ne garde
  // que la meilleure vérification par référence.
  const byNumber = new Map<string, VerifiedReference>();
  for (const r of scoped) {
    const k = `${r.kind}:${normalizeRef(r.number)}`;
    const cur = byNumber.get(k);
    if (!cur || (STATUS_RANK[r.status] ?? 0) > (STATUS_RANK[cur.status] ?? 0)) byNumber.set(k, r);
  }

  // On masque le bruit : si tout est "unverified", l'information n'apporte rien.
  // On écarte en outre les entrées non affichables (artefacts d'extraction et
  // vérifications échouées sans libellé exploitable) : elles produisaient des
  // placeholders du type « article 1 (vérification indisponible) ».
  const displayable = Array.from(byNumber.values()).filter(isDisplayableReference);
  return displayable.some((r) => r.status !== "unverified") ? displayable : [];
}

/** Statuts issus d'une confrontation effective à une base officielle. */
const OFFICIALLY_CHECKED = new Set([
  "confirmed",
  "mismatch",
  "abrogated",
  "not_found",
  "not_consolidated",
]);

/**
 * Nombre de références RÉELLEMENT confrontées à une base officielle parmi
 * celles affichées. Sert de compteur au bloc « Sources ».
 */
export function countOfficiallyChecked(
  references: readonly VerifiedReference[] | undefined,
): number {
  return (references ?? []).filter((r) => OFFICIALLY_CHECKED.has(r.status)).length;
}

/**
 * QA 09/09/2026 — BUG 2. « N références vérifiées sur Légifrance » comptait
 * aussi les références NON RETROUVÉES : le panneau annonçait « 12 vérifiées »
 * en listant en tête « non retrouvée dans la base officielle ». Une référence
 * n'est vérifiée que si la base officielle l'a CONFIRMÉE.
 */
export function countConfirmedChecked(
  references: readonly VerifiedReference[] | undefined,
): number {
  return (references ?? []).filter((r) => r.status === "confirmed").length;
}

/** Références analysées mais introuvables dans la base officielle. */
export function countNotFoundChecked(
  references: readonly VerifiedReference[] | undefined,
): number {
  return (references ?? []).filter((r) => r.status === "not_found").length;
}


const STATUS_RANK: Record<string, number> = {
  confirmed: 5,
  mismatch: 4,
  abrogated: 3,
  not_found: 2,
  not_applicable: 2,
  not_consolidated: 2,
  unverified: 1,
};

const ReferenceCheckBadges = ({
  references,
  body,
  unanchored,
  vagueAttributions,
  unnumberedDecisions,
  nonConformingQuotes,
}: {
  references: VerifiedReference[];
  /** Corps de la réponse : sert à écarter les références non mobilisées. */
  body?: string;
  /**
   * RE-TEST 09/09/2026 — numéros d'articles dont un passage cité entre
   * guillemets diverge du texte officiel rapatrié. Statut dédié, visuellement
   * distinct de « en vigueur ».
   */
  nonConformingQuotes?: string[];
  /**
   * DISCIPLINE DE CITATION — numéros cités dans le corps qui ne figurent PAS
   * parmi les sources officielles injectées. Signalement informatif : ambre,
   * jamais rouge, jamais bloquant.
   */
  unanchored?: string[];
  /**
   * Formules d'autorité jurisprudentielle sans décision identifiable
   * (« la jurisprudence administrative rappelle que… »). Marqueur sobre.
   */
  vagueAttributions?: VagueAttribution[];
  /**
   * AUDIT QA — décisions citées avec une date mais SANS numéro : le
   * rapprochement officiel se fait par numéro de pourvoi ou de requête, elles
   * ne peuvent donc pas être recoupées automatiquement. Elles doivent le dire.
   */
  unnumberedDecisions?: UnnumberedDecision[];
}) => {
  const vagueList = (vagueAttributions ?? []).slice(0, 3);
  const unnumberedList = (unnumberedDecisions ?? []).slice(0, 4);
  const list = selectDisplayedReferences(references, body);
  const nonConforming = new Set((nonConformingQuotes ?? []).map(normalizeRef));

  // QA 30/08/2026 — BUG 6. Une référence déjà listée avec son statut officiel
  // (« article 65 — texte non codifié ») ne doit pas réapparaître dans les
  // mentions « non ancrées » avec un second statut contradictoire.
  const listedNumbers = new Set(list.map((r) => normalizeRef(r.number)));
  const unanchoredList = (unanchored ?? [])
    .filter((n) => {
      const k = numberKeyOf(n);
      return k.length > 0 && !listedNumbers.has(k);
    })
    .slice(0, 6);

  if (list.length === 0 && unanchoredList.length === 0 && vagueList.length === 0 && unnumberedList.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="Vérification des références officielles">
      {list.map((ref, i) => {
        const style = STYLES[ref.status] ?? STYLES.unverified;
        // PORTÉE NON RECOUPÉE (QA 30/08/2026, BUG 2) : une décision peut être
        // confirmée dans son existence sans que sa solution ait été lue. Le
        // dire explicitement plutôt que d'afficher une validation pleine.
        const scopeUnverified = hasCaselawScopeDivergence(ref, body ?? "");
        // QA 09/09/2026 — BUG 4. Numéro cité dans son ANCIENNE rédaction :
        // la base officielle retrouve l'article ACTUEL portant le même numéro
        // et affichait « en vigueur » sur une disposition sans rapport.
        const citedAsFormer =
          ref.kind === "article" &&
          ref.status === "confirmed" &&
          isCitedAsFormerArticle(ref.number, body ?? "");
        // Citation présentée comme le texte de l'article mais divergente du
        // texte officiel : ce défaut prime sur tout autre libellé d'état.
        const quoteNonConforming =
          ref.kind === "article" && nonConforming.has(normalizeRef(ref.number));
        const Icon = quoteNonConforming ? AlertTriangle : style.icon;
        const note = quoteNonConforming
          ? "citation non conforme au texte en vigueur"
          : citedAsFormer
          ? FORMER_ARTICLE_NOTE
          : scopeUnverified
            ? CASELAW_SCOPE_NOTE
            : typeof style.note === "function"
              ? style.note(ref)
              : style.note;
        // DATE DE VERSION — issue exclusivement des métadonnées officielles.
        // Absente des métadonnées ⇒ rien n'est affiché (jamais d'invention).
        // UN SEUL STATUT PAR RÉFÉRENCE (QA 25/08/2026) : si le libellé
        // officiel porte déjà un état (« en vigueur, abrogation différée »…),
        // on ne juxtapose pas un second état.
        const mainLabel = citedAsFormer || quoteNonConforming ? referenceLabel(ref) : style.label(ref);
        const labelCarriesState = /en vigueur|abrog|p[ée]rim/i.test(String(mainLabel ?? ""));
        const vigueur =
          citedAsFormer || quoteNonConforming || labelCarriesState ? undefined : inForceLabel(ref);
        // DRAPEAU DE RÉCENCE — texte dont la version officielle est postérieure
        // à la période de connaissance du modèle : contenu à recouper.
        const recent = citedAsFormer || quoteNonConforming ? undefined : recentModificationLabel(ref);
        // Avertissement tant que le texte réel n'a pas été lu ; confirmation
        // (ton neutre) une fois la réponse réconciliée sur la version en vigueur.
        const recentPending = isPendingRecentModification(ref);
        const content = (
          <>
            <Icon className="w-3 h-3 flex-shrink-0" />
            <span className="truncate max-w-[280px]">{mainLabel}</span>
            {note && <span className="opacity-70 font-normal">({note})</span>}
            {vigueur && <span className="opacity-70 font-normal">· {vigueur}</span>}
            {recent && (
              <span
                className={`font-semibold ${
                  recentPending
                    ? "text-amber-700 dark:text-amber-300"
                    : "text-green-700 dark:text-green-400"
                }`}
              >
                · {recent}
              </span>
            )}
          </>
        );
        const cls = `inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium ${
          quoteNonConforming
            ? "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30"
            : citedAsFormer || recentPending || scopeUnverified
            ? "bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30"
            : style.cls
        }`;




        return ref.officialUrl ? (
          <a
            key={`${ref.raw}-${i}`}
            href={ref.officialUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${cls} hover:underline`}
            title={ref.officialTitle || "Consulter sur Légifrance"}
          >
            {content}
          </a>
        ) : (
          <span key={`${ref.raw}-${i}`} className={cls} title={ref.officialTitle}>
            {content}
          </span>
        );
      })}
      {unanchoredList.map((label, i) => (
        <span
          key={`unanchored-${i}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30"
          title="Ce numéro ne figure pas parmi les sources officielles consultées pour cette réponse."
        >
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          <span className="truncate max-w-[280px]">{label}</span>
          <span className="opacity-70 font-normal">(non présent dans les sources consultées)</span>
        </span>
      ))}
      {unnumberedList.map((d, i) => (
        <span
          key={`unnumbered-${i}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30"
          title="Décision citée sans numéro de pourvoi ou de requête : le recoupement automatique sur la base officielle se fait par numéro. À confirmer manuellement."
        >
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          <span className="truncate max-w-[280px]">{d.label}</span>
          <span className="opacity-70 font-normal">(non recoupée automatiquement — à confirmer)</span>
        </span>
      ))}
      {vagueList.map((item, i) => (
        <span
          key={`vague-${i}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium bg-muted text-muted-foreground border-border"
          title="Cette affirmation invoque une autorité jurisprudentielle sans citer de décision identifiable."
        >
          <Info className="w-3 h-3 flex-shrink-0" />
          <span className="truncate max-w-[280px]">{vagueAttributionLabel(item)}</span>
        </span>
      ))}
    </div>
  );
};

export default ReferenceCheckBadges;
